import type {
  ManifestField,
  ManifestProblem,
  NodeCodeResponse,
  NodeManifestSummary,
} from '@/services/workflow/node-code';
import type { NodeStruct } from '@/types/node-types';

/**
 * The Custom Code (Lambda) node's `node.json`, shaped for people.
 *
 * The backend serves a ready summary as `NodeCodeResponse.manifest` (built
 * with the node's own parser). A backend without that field still returns
 * the script's files, including `node.json`, so this module derives an
 * equivalent summary client-side: the Script card and the node-body rows
 * work on either backend, and the server-side version simply has the final
 * say on validity when present.
 *
 * Mirrors `services/software_repo/manifest_summary.py`: one row per field
 * from `properties` and `required`, nested objects one level deep, a
 * non-object schema shown as one `(value)` row.
 */

export const CUSTOM_CODE_NODE_ID = 'custom_code_node';
export const MANIFEST_FILENAME = 'node.json';

/** Schema keywords the platform does not check at run time. */
export const UNENFORCED_SCHEMA_KEYWORDS = [
  'enum',
  'const',
  'minimum',
  'maximum',
  'exclusiveMinimum',
  'exclusiveMaximum',
  'minLength',
  'maxLength',
  'pattern',
  'format',
  'minItems',
  'maxItems',
  'uniqueItems',
  'additionalProperties',
  'oneOf',
  'anyOf',
  'allOf',
  'not',
  'if',
  'then',
  'else',
  '$ref',
  'dependencies',
  'dependentRequired',
  'patternProperties',
] as const;

const MAX_FIELD_DEPTH = 2;
const MAX_FIELDS_PER_LEVEL = 64;
const SUPPORTED_RUNTIMES = ['python', 'node'] as const;

type Json = Record<string, unknown>;

const asRecord = (value: unknown): Json | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Json)
    : null;

const isObjectSchema = (schema: unknown): boolean => {
  const record = asRecord(schema);
  if (!record) return false;
  if (record.type === 'object') return true;
  if (record.type === undefined || record.type === null) {
    return (
      asRecord(record.properties) !== null || Array.isArray(record.required)
    );
  }
  return false;
};

/** A short, plain type label for one schema node ("array of number"). */
export const typeLabel = (schema: unknown): string => {
  const record = asRecord(schema);
  if (!record) return 'any';
  let declared = record.type;
  if (Array.isArray(declared)) {
    const names = declared.filter((n): n is string => typeof n === 'string');
    return names.length ? names.join(' or ') : 'any';
  }
  if (typeof declared !== 'string' || !declared) {
    if (asRecord(record.properties) || 'required' in record) return 'object';
    if ('items' in record) declared = 'array';
    else return 'any';
  }
  if (declared === 'array') {
    const inner = asRecord(record.items) ? typeLabel(record.items) : 'any';
    return inner === 'any' ? 'array' : `array of ${inner}`;
  }
  return String(declared);
};

/** Flatten an object schema into table rows (properties + required names). */
export const schemaFields = (schema: unknown, depth = 1): ManifestField[] => {
  const record = asRecord(schema);
  if (!record || !isObjectSchema(record)) return [];
  const properties = asRecord(record.properties) ?? {};
  const required = (
    Array.isArray(record.required) ? record.required : []
  ).filter((n): n is string => typeof n === 'string' && n.length > 0);
  const names: string[] = [];
  for (const name of [...Object.keys(properties), ...required]) {
    if (!names.includes(name)) names.push(name);
  }
  return names.slice(0, MAX_FIELDS_PER_LEVEL).map((name) => {
    const sub = asRecord(properties[name]);
    const row: ManifestField = {
      name,
      type: sub ? typeLabel(sub) : 'any',
      required: required.includes(name),
      description:
        sub && typeof sub.description === 'string' ? sub.description : '',
    };
    if (sub && depth < MAX_FIELD_DEPTH && typeLabel(sub) === 'object') {
      const children = schemaFields(sub, depth + 1);
      if (children.length) row.children = children;
    }
    return row;
  });
};

/** The single row for a schema that is not an object (array, string...). */
export const schemaValueRow = (schema: unknown): ManifestField | null => {
  const record = asRecord(schema);
  if (!record || Object.keys(record).length === 0) return null;
  if (isObjectSchema(record)) return null;
  const description = record.description;
  return {
    name: '(value)',
    type: typeLabel(record),
    required: true,
    description: typeof description === 'string' ? description : '',
  };
};

export const unenforcedKeywords = (schema: unknown, depth = 0): string[] => {
  const record = asRecord(schema);
  const found: string[] = [];
  if (!record || depth > 8) return found;
  const add = (keys: string[]) => {
    for (const key of keys) if (!found.includes(key)) found.push(key);
  };
  for (const [key, value] of Object.entries(record)) {
    if ((UNENFORCED_SCHEMA_KEYWORDS as readonly string[]).includes(key)) {
      add([key]);
    }
    if (
      (key === 'properties' || key === 'patternProperties') &&
      asRecord(value)
    ) {
      for (const sub of Object.values(value as Json)) {
        add(unenforcedKeywords(sub, depth + 1));
      }
    } else if (key === 'items' && asRecord(value)) {
      add(unenforcedKeywords(value, depth + 1));
    }
  }
  return found;
};

const problem = (
  code: string,
  message: string,
  severity: ManifestProblem['severity'] = 'error',
): ManifestProblem => ({ code, severity, message });

const emptySummary = (scriptPath: string): NodeManifestSummary => ({
  present: false,
  valid: false,
  manifest_path: `${scriptPath}/${MANIFEST_FILENAME}`,
  name: '',
  description: '',
  runtime: null,
  entrypoint: '',
  entrypoint_present: false,
  timeout_seconds: null,
  install_timeout_seconds: null,
  allowed_hosts: [],
  network_isolated: true,
  inputs: [],
  input_value: null,
  outputs: [],
  output_value: null,
  input_schema: null,
  output_schema: null,
  input_files: null,
  output_files: null,
  unenforced_schema_keywords: [],
  raw: null,
  problems: [],
});

const positiveInt = (value: unknown): number | null =>
  typeof value === 'number' && Number.isInteger(value) && value > 0
    ? value
    : null;

/**
 * Build the summary from the script files (client-side twin of the backend).
 * Used when the backend response has no `manifest` field.
 */
export const summarizeManifestFromFiles = (
  files: Array<{ relative_path: string; content: string }>,
  scriptPath: string,
): NodeManifestSummary => {
  const summary = emptySummary(scriptPath);
  const manifestFile = files.find((f) => f.relative_path === MANIFEST_FILENAME);
  if (!manifestFile) {
    summary.problems.push(
      problem(
        'manifest_missing',
        `There is no ${MANIFEST_FILENAME} in ${scriptPath} at the pinned commit. Add one with at least name, entrypoint and runtime ("python" or "node"), commit, and pin the new version.`,
      ),
    );
    return summary;
  }
  summary.present = true;
  let raw: Json;
  try {
    const parsed: unknown = JSON.parse(manifestFile.content);
    const record = asRecord(parsed);
    if (!record) throw new Error('not an object');
    raw = record;
  } catch (error) {
    summary.problems.push(
      problem(
        'manifest_invalid',
        `${summary.manifest_path} is not a valid JSON object${
          error instanceof Error && error.message !== 'not an object'
            ? `: ${error.message}`
            : '.'
        }`,
      ),
    );
    return summary;
  }
  const missing = ['name', 'entrypoint', 'runtime'].filter(
    (key) => !String(raw[key] ?? '').trim(),
  );
  if (missing.length) {
    summary.problems.push(
      problem(
        'manifest_invalid',
        `${summary.manifest_path} is missing required key(s): ${missing.join(', ')}.`,
      ),
    );
    return summary;
  }
  const runtime = String(raw.runtime).trim().toLowerCase();
  if (!(SUPPORTED_RUNTIMES as readonly string[]).includes(runtime)) {
    summary.problems.push(
      problem(
        'manifest_invalid',
        `${summary.manifest_path} declares runtime '${String(raw.runtime)}', but only ${SUPPORTED_RUNTIMES.join(', ')} are supported.`,
      ),
    );
    return summary;
  }
  const entrypoint = String(raw.entrypoint).trim();
  if (runtime === 'node' && !/\.(js|mjs)$/.test(entrypoint)) {
    summary.problems.push(
      problem(
        'manifest_invalid',
        `${summary.manifest_path}: runtime 'node' requires a .js or .mjs entrypoint, got '${entrypoint}'.`,
      ),
    );
    return summary;
  }

  summary.valid = true;
  summary.raw = raw;
  summary.name = String(raw.name).trim();
  summary.description =
    typeof raw.description === 'string' ? raw.description : '';
  summary.runtime = runtime;
  summary.entrypoint = entrypoint;
  summary.entrypoint_present = files.some(
    (f) => f.relative_path === entrypoint,
  );
  summary.timeout_seconds = positiveInt(raw.timeout_seconds);
  summary.install_timeout_seconds = positiveInt(raw.install_timeout_seconds);
  const network = asRecord(raw.network);
  summary.allowed_hosts = (
    Array.isArray(network?.allowed_hosts) ? network.allowed_hosts : []
  )
    .filter((h): h is string => typeof h === 'string')
    .map((h) => h.trim())
    .filter(Boolean);
  summary.network_isolated = summary.allowed_hosts.length === 0;
  summary.input_schema = asRecord(raw.input_schema);
  summary.output_schema = asRecord(raw.output_schema);
  summary.inputs = schemaFields(summary.input_schema);
  summary.input_value = schemaValueRow(summary.input_schema);
  summary.outputs = schemaFields(summary.output_schema);
  summary.output_value = schemaValueRow(summary.output_schema);
  const inputFiles = asRecord(raw.input_files);
  if (inputFiles) {
    summary.input_files = {
      required: inputFiles.required === true,
      max_count: positiveInt(inputFiles.max_count),
      patterns: (Array.isArray(inputFiles.patterns)
        ? inputFiles.patterns
        : []
      ).filter((p): p is string => typeof p === 'string'),
    };
  }
  const outputFiles = asRecord(raw.output_files);
  if (outputFiles) {
    summary.output_files = { max_count: positiveInt(outputFiles.max_count) };
  }
  const keywords: string[] = [];
  for (const schema of [summary.input_schema, summary.output_schema]) {
    for (const k of unenforcedKeywords(schema)) {
      if (!keywords.includes(k)) keywords.push(k);
    }
  }
  summary.unenforced_schema_keywords = keywords;

  if (!summary.entrypoint_present) {
    summary.problems.push(
      problem(
        'entrypoint_missing',
        `node.json names '${entrypoint}' as the entrypoint, but that file is not in the script directory at the pinned commit. Add the file or correct the entrypoint, then commit and pin.`,
      ),
    );
  }
  if (!summary.input_schema) {
    summary.problems.push(
      problem(
        'input_schema_missing',
        'The script does not declare an input_schema, so any JSON is accepted and the Inputs list cannot be shown. Add input_schema with the fields the script reads.',
        'warning',
      ),
    );
  } else if (!summary.inputs.length && !summary.input_value) {
    summary.problems.push(
      problem(
        'input_schema_unnamed',
        'input_schema declares no fields (no properties or required), so the Inputs list is empty. Name the fields the script reads.',
        'warning',
      ),
    );
  }
  if (!summary.output_schema) {
    summary.problems.push(
      problem(
        'output_schema_missing',
        'The script does not declare an output_schema, so downstream nodes cannot see what it produces. Add output_schema with the fields the script writes.',
        'warning',
      ),
    );
  }
  if (keywords.length) {
    summary.problems.push(
      problem(
        'schema_keywords_unenforced',
        `node.json uses ${keywords.join(', ')}, which the platform does not check at run time (only type, required and per-field type are checked). The script must enforce these itself.`,
        'info',
      ),
    );
  }
  if (summary.allowed_hosts.length) {
    const shown = summary.allowed_hosts.slice(0, 5).join(', ');
    summary.problems.push(
      problem(
        'network_enabled',
        `The script runs with internet access on because node.json declares allowed hosts (${shown}${
          summary.allowed_hosts.length > 5 ? '…' : ''
        }). Remove them if the script does not need the network.`,
        'info',
      ),
    );
  }
  return summary;
};

/** True when the backend's `manifest` is the full card shape this UI reads. */
const isCardSummary = (value: unknown): value is NodeManifestSummary => {
  const record = asRecord(value);
  return Boolean(
    record &&
      typeof record.present === 'boolean' &&
      typeof record.valid === 'boolean' &&
      Array.isArray(record.inputs) &&
      Array.isArray(record.outputs) &&
      Array.isArray(record.problems),
  );
};

/**
 * The backend's summary when it is the card shape, else the client-side
 * derivation from `files`. A backend that sends a different `manifest`
 * object (an older or partial shape) is treated like one that sends none,
 * so the card never reads fields that are not there.
 */
export const manifestSummaryFor = (
  response: NodeCodeResponse,
): NodeManifestSummary | null => {
  if (isCardSummary(response.manifest)) return response.manifest;
  if (!response.pinned) return null;
  return summarizeManifestFromFiles(response.files, response.script_path);
};

/** Pin state read from the node's own params (no network). */
export const getCustomCodePin = (
  struct: NodeStruct,
): { scriptPath: string; scriptSha: string; pinned: boolean } => {
  const value = (fieldId: string): string => {
    const param = struct.params?.find((p) => p.field_id === fieldId);
    return typeof param?.value === 'string' ? param.value.trim() : '';
  };
  const scriptPath = value('script_path');
  const scriptSha = value('script_sha');
  return { scriptPath, scriptSha, pinned: Boolean(scriptPath && scriptSha) };
};

export const shortSha = (sha: string, length = 8): string =>
  sha ? sha.slice(0, length) : '';

export interface ManifestLine {
  direction: 'in' | 'out';
  text: string;
}

/**
 * One compact line per declared input and output for the canvas node body
 * ("in: n (any, required)", "out: (value) array of boolean").
 */
export const manifestLines = (
  summary: NodeManifestSummary,
  limit = 8,
): { lines: ManifestLine[]; hidden: number } => {
  const describe = (field: ManifestField): string =>
    `${field.name} (${field.type}${field.required ? ', required' : ''})`;
  const lines: ManifestLine[] = [];
  const inputs = summary.input_value ? [summary.input_value] : summary.inputs;
  const outputs = summary.output_value
    ? [summary.output_value]
    : summary.outputs;
  for (const field of inputs)
    lines.push({ direction: 'in', text: describe(field) });
  for (const field of outputs) {
    lines.push({ direction: 'out', text: describe(field) });
  }
  return {
    lines: lines.slice(0, limit),
    hidden: Math.max(0, lines.length - limit),
  };
};

/** Plain words for the typed errors the code-read route can return. */
export const describeNodeCodeError = (error: {
  code?: string;
  message: string;
}): string => {
  switch (error.code) {
    case 'repository_missing':
      return 'This workflow has no software repository attached, so the script cannot be read. Attach one in the workflow settings.';
    case 'repository_inaccessible':
    case 'credential_error':
    case 'no_credential':
      return 'The repository could not be read with the organization\u2019s git credential. An org admin can fix the GitHub connection in settings.';
    case 'sha_not_found':
      return 'The pinned commit does not exist in the repository. Pin a commit that exists.';
    case 'path_not_found':
      return 'The script directory does not exist at the pinned commit. Check the script path.';
    case 'feature_disabled':
      return 'Software repositories are switched off on this deployment.';
    default:
      return error.message || 'The script could not be read.';
  }
};
